import { z } from 'zod';
import { effectRefSchema } from '../rules/effect.js';
/**
 * Счётчик ресурса — данные записи контента (D56).
 *
 * Лежит рядом с эффектами и по той же причине: ярости варвара,
 * вдохновение барда и Божественный канал описаны в источнике числом,
 * а числа в коде не живут. Эффектом ресурс при этом не является —
 * эффект меняет вычисляемую величину листа, а счётчик хранит
 * состояние между отдыхами, и подмешивать время в чистую функцию
 * расчёта нельзя (rules-engine.md, раздел 1).
 *
 * Определение живёт здесь, текущее значение — шагами журнала (D4),
 * как хиты и ячейки.
 */
/**
 * Максимум: число либо ссылка на величину листа.
 *
 * Ссылка решает и задачу повышения уровня. Бонус мастерства,
 * модификатор характеристики и заведённая ради ресурса величина
 * (`resource.rage`) — обычные величины листа: они пересчитываются
 * свёрткой на каждое открытие, а значит, и максимум вместе с ними.
 * Отдельного «пересчитать при повышении» нет, и забыть его вызвать
 * негде.
 */
export const resourceMaxSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('number'),
        value: z.number().int().positive(),
    }),
    z.strictObject({
        kind: z.literal('ref'),
        /** Путь величины: `proficiency_bonus`, `abilities.cha.mod`. */
        path: effectRefSchema.shape.ref,
        /**
         * Нижняя граница: «a number of times equal to your Charisma
         * modifier (minimum of once)» (с. 30). `null` — границы нет.
         */
        min: z.number().int().nonnegative().nullable(),
    }),
]);
/**
 * Чем восстанавливается: длинным отдыхом, коротким или ничем.
 *
 * У короткого есть ещё число. «You regain one expended use when you
 * finish a Short Rest, and you regain all expended uses when you
 * finish a Long Rest» — короткий возвращает одно использование,
 * а не все, и так сформулированы пять записей SRD. Свести их
 * к «восстанавливается коротким отдыхом» значило бы выдавать варвару
 * лишнюю ярость на каждом привале.
 *
 * Долгий отдых возвращает всё, у чего восстановление есть:
 * `none` не чинится ни коротким отдыхом, ни долгим.
 */
export const resourceRecoverySchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('none') }),
    z.strictObject({ kind: z.literal('long_rest') }),
    z.strictObject({
        kind: z.literal('short_rest'),
        /** Сколько возвращает короткий отдых; `null` — все. */
        uses: z.number().int().positive().nullable(),
    }),
]);
export const resourceSchema = z.strictObject({
    /** Ключ в пределах записи: `rage`, `bardic_inspiration`. */
    key: z.string().regex(/^[a-z0-9_]+$/),
    name: z.string().min(1),
    max: resourceMaxSchema,
    recovery: resourceRecoverySchema,
});
/**
 * Идентификатор счётчика на листе: запись плюс ключ ресурса.
 *
 * Своя запись человека (D56) идентификатора контента не имеет вовсе,
 * и её счётчик зовётся `own:<ключ>`. Разделять их формой, а не флагом,
 * нужно затем, что по идентификатору ищут определение: у одного оно
 * в записи контента, у другого — в шаге журнала.
 */
export const resourceIdSchema = z
    .string()
    .regex(/^(srd:[a-z0-9_]+#[a-z0-9_]+|u:[a-z0-9_]+:[a-z0-9_]+#[a-z0-9_]+|own:[a-z0-9_]+)$/, 'ожидается <запись>#<ключ> или own:<ключ>');
/** Идентификатор счётчика записи контента. */
export function resourceIdOf(contentId, key) {
    return `${contentId}#${key}`;
}
/** Идентификатор своего счётчика (D56). */
export function ownResourceId(key) {
    return `own:${key}`;
}
//# sourceMappingURL=resource.js.map